import React, { useEffect, useState } from "react";
import { getWidgets } from "@/lib/api";
import "./BackgroundWidgets.css";

/**
 * Owner-customisable background widgets, voice-added via add_widget action.
 * Polls /api/widgets every 4s so changes from voice land on screen near-instantly,
 * and also responds to a "widgets-changed" event for instant local refresh.
 */
export default function BackgroundWidgets() {
  const [widgets, setWidgets] = useState([]);
  const [positions, setPositions] = useState(() => {
    try { return JSON.parse(localStorage.getItem("rawco_widget_positions") || "{}"); } catch { return {}; }
  });
  // Per-widget custom size {width, height}. Set when the user drags the
  // corner handle. Persists in localStorage so widgets stay exactly the
  // size you left them.
  const [sizes, setSizes] = useState(() => {
    try { return JSON.parse(localStorage.getItem("rawco_widget_sizes") || "{}"); } catch { return {}; }
  });

  useEffect(() => {
    let stop = false;
    let lastSig = "";
    const refresh = async () => {
      try {
        const w = await getWidgets();
        if (stop) return;
        // Only re-render if the widget list actually changed — avoids
        // fighting an in-progress drag. This restores polling so newly-
        // added widgets appear without needing a manual page reload.
        const sig = JSON.stringify(Array.isArray(w) ? w : []);
        if (sig !== lastSig) {
          lastSig = sig;
          setWidgets(Array.isArray(w) ? w : []);
        }
      } catch {}
    };
    refresh();
    const poll = setInterval(refresh, 5000);
    const onChange = () => refresh();
    window.addEventListener("rawco-widgets-changed", onChange);
    return () => {
      stop = true;
      clearInterval(poll);
      window.removeEventListener("rawco-widgets-changed", onChange);
    };
  }, []);

  const updatePosition = (id, x, y) => {
    // Clamp so a widget can never be saved off-screen (root cause of the
    // "unmovable" complaint — once it drifted past the edge it was lost).
    const PAD = 12;
    const minVisible = 80;
    const W = typeof window !== "undefined" ? window.innerWidth  : 1920;
    const H = typeof window !== "undefined" ? window.innerHeight : 1080;
    const safeX = Math.max(PAD, Math.min(W - minVisible, x));
    const safeY = Math.max(PAD, Math.min(H - minVisible, y));
    setPositions(prev => {
      const next = { ...prev, [id]: { x: safeX, y: safeY } };
      try { localStorage.setItem("rawco_widget_positions", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const resetPosition = (id) => {
    setPositions(prev => {
      const next = { ...prev };
      delete next[id];
      try { localStorage.setItem("rawco_widget_positions", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Expose a global escape hatch so the owner can recover stuck widgets via
  // voice or console: window.rawcoResetWidgetPositions()
  if (typeof window !== "undefined") {
    window.rawcoResetWidgetPositions = () => {
      try { localStorage.removeItem("rawco_widget_positions"); } catch {}
      setPositions({});
    };
  }

  // Split widgets: those with custom positions render free-floating; the rest in their anchored stacks.
  const freeWidgets = widgets.filter(w => positions[w.id]);
  const stackedWidgets = widgets.filter(w => !positions[w.id]);

  const buckets = {
    top_left: [], top_center: [], top_right: [],
    bottom_left: [], bottom_center: [], bottom_right: [],
  };
  for (const w of stackedWidgets) {
    const p = buckets[w.position] ? w.position : "top_right";
    buckets[p].push(w);
  }

  return (
    <>
      {Object.entries(buckets).map(([pos, list]) =>
        list.length ? (
          <div key={pos} className={`bg-widget-stack pos-${pos}`} data-testid={`widget-stack-${pos}`}>
            {list.map(w => <WidgetCell key={w.id} w={w} onDragEnd={updatePosition} positions={positions} />)}
          </div>
        ) : null
      )}
      {freeWidgets.map(w => <WidgetCell key={w.id} w={w} onDragEnd={updatePosition} positions={positions} free />)}
    </>
  );
}

function WidgetCell({ w, onDragEnd, positions, free = false }) {
  const wrapRef = React.useRef(null);
  const draggedRef = React.useRef(false);
  const sizeClass = `wsize-${w.size || "medium"}`;
  const style = w.color ? { color: w.color, borderColor: w.color } : undefined;
  let body;
  switch (w.kind) {
    case "clock":        body = <ClockWidget className={sizeClass} style={style} />; break;
    case "date":         body = <DateWidget  className={sizeClass} style={style} />; break;
    case "weather":      body = <WeatherWidget className={sizeClass} style={style} />; break;
    case "btc_ticker":   body = <CryptoTicker symbol="bitcoin"  label="BTC" className={sizeClass} style={style} />; break;
    case "eth_ticker":   body = <CryptoTicker symbol="ethereum" label="ETH" className={sizeClass} style={style} />; break;
    case "gold_ticker":  body = <GoldTicker className={sizeClass} style={style} />; break;
    case "stock_ticker": body = <StockTicker symbol={w.symbol || "TSLA"} className={sizeClass} style={style} />; break;
    case "custom_text":  body = <CustomText text={w.text || ""} className={sizeClass} style={style} />; break;
    case "logo_image":   body = <LogoImage url={w.url} className={sizeClass} />; break;
    case "illuminati_eye": body = <IlluminatiEye className={sizeClass} style={style} />; break;
    case "moon_phase":   body = <MoonPhase className={sizeClass} style={style} />; break;
    case "quote":        body = <CustomText text={`"${w.text || "Stay focused."}"`} className={`${sizeClass} italic`} style={style} />; break;
    case "live_users":   body = <LiveUsersWidget appKey={w.app_key || "all"} className={sizeClass} style={style} />; break;
    case "dashboard_iframe": body = <DashboardIframeWidget url={w.url} title={w.title} className={sizeClass} />; break;
    default: return null;
  }

  const removeMe = async (e) => {
    e.stopPropagation(); e.preventDefault();
    try {
      const tk = localStorage.getItem("rawco_token");
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/widgets/${w.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${tk}` },
      });
      window.dispatchEvent(new Event("rawco-widgets-changed"));
      // 🔁 Owner Feb 2026: "I still can't delete my widgets off my mirror"
      // — delete fired on the mirror tab but the BackgroundWidgets list
      // didn't refresh because the dispatch only hit the current tab.
      // Broadcast across all tabs (orb + mirror) so whichever you
      // deleted from, both refresh instantly.
      try {
        const bc = new BroadcastChannel("rawco-stage");
        bc.postMessage({ type: "widgets-changed" });
        bc.close();
      } catch { /* ignore — older browsers */ }
    } catch (err) {
      console.warn("[BackgroundWidgets] delete failed", err);
    }
  };

  const startDrag = (clientX, clientY) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const offsetX = clientX - rect.left;
    const offsetY = clientY - rect.top;
    draggedRef.current = false;
    let rafPending = false;
    let lastNewX = rect.left, lastNewY = rect.top;

    const onMove = (cx, cy) => {
      lastNewX = Math.max(8, Math.min(window.innerWidth - rect.width - 8, cx - offsetX));
      lastNewY = Math.max(8, Math.min(window.innerHeight - rect.height - 8, cy - offsetY));
      draggedRef.current = true;
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(() => {
          rafPending = false;
          onDragEnd?.(w.id, lastNewX, lastNewY); // live update — re-renders are smooth
        });
      }
    };

    const onMouseMove = (e) => onMove(e.clientX, e.clientY);
    const onTouchMove = (e) => { if (e.touches?.[0]) onMove(e.touches[0].clientX, e.touches[0].clientY); };
    const finish = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", finish);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", finish);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", finish);
    window.addEventListener("touchmove", onTouchMove);
    window.addEventListener("touchend", finish);
  };

  const onMouseDown = (e) => {
    // Don't initiate drag from the × delete button — let its click fire
    if (e.target.closest(".bg-widget-remove")) return;
    e.preventDefault();
    // Update React state live during drag so polling re-renders don't snap the
    // element back. Switch the wrapper to "absolutely positioned" the moment
    // the drag starts so layout reflows don't fight us.
    const el = wrapRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      onDragEnd?.(w.id, rect.left, rect.top); // promote to free-floating immediately
    }
    startDrag(e.clientX, e.clientY);
  };
  const onTouchStart = (e) => {
    if (e.target.closest(".bg-widget-remove")) return;
    const t = e.touches?.[0];
    if (!t) return;
    const el = wrapRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      onDragEnd?.(w.id, rect.left, rect.top);
    }
    startDrag(t.clientX, t.clientY);
  };

  const savedPos = positions?.[w.id];
  // Clamp saved position to viewport so a widget can never be stranded
  // off-screen and become un-grabbable.
  const PAD = 12;
  const minVisible = 80; // at least this many pixels of the widget must be on-screen
  const safeSavedPos = savedPos ? {
    x: Math.max(PAD, Math.min((typeof window !== "undefined" ? window.innerWidth  : 1920) - minVisible, savedPos.x)),
    y: Math.max(PAD, Math.min((typeof window !== "undefined" ? window.innerHeight : 1080) - minVisible, savedPos.y)),
  } : null;
  // 🛡 Owner Feb 2026: "I still can't delete my widgets on the mirror."
  // The × delete button is absolutely positioned at top:-12px / right:-12px
  // (12px OUTSIDE the wrap). When the wrap has `overflow-y: auto` the
  // negative offsets get clipped → button rendered but unclickable.
  // Drop overflow:auto here and let the inner widget component manage
  // its own scrolling if it really needs it. Keeps the × visible AND
  // clickable on both orb and mirror.
  const wrapStyle = safeSavedPos
    ? { position: "fixed", left: safeSavedPos.x, top: safeSavedPos.y, right: "auto", bottom: "auto", zIndex: 4, maxHeight: "calc(100vh - 24px)" }
    : { maxHeight: "calc(100vh - 24px)" };

  return (
    <div
      ref={wrapRef}
      className="bg-widget-wrap group"
      data-testid={`widget-wrap-${w.id}`}
      style={wrapStyle}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
    >
      {body}
      <button
        onClick={removeMe}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        className="bg-widget-remove"
        data-testid={`widget-remove-${w.id}`}
        aria-label="Remove widget"
        title="Click to remove this widget"
      >
        ×
      </button>
    </div>
  );
}

/* ----- Individual widgets ----- */
function ClockWidget({ className, style }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <div className={`bg-widget bg-widget-clock ${className}`} style={style} data-testid="widget-clock">
      <div className="font-display tabular-nums rc-glow-text wval">{now.toTimeString().slice(0, 8)}</div>
    </div>
  );
}

function DateWidget({ className, style }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);
  return (
    <div className={`bg-widget bg-widget-date ${className}`} style={style} data-testid="widget-date">
      <div className="font-display rc-glow-text wval">{now.toDateString().toUpperCase()}</div>
    </div>
  );
}

function WeatherWidget({ className, style }) {
  const [w, setW] = useState(null);
  useEffect(() => {
    const fetchW = async () => {
      try {
        const tk = localStorage.getItem("rawco_token");
        if (!tk) return;
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/weather`, { headers: { Authorization: `Bearer ${tk}` } });
        if (r.ok) setW(await r.json());
      } catch {}
    };
    fetchW();
    const t = setInterval(fetchW, 600000); // 10min
    return () => clearInterval(t);
  }, []);
  if (!w) return null;
  // Pick graphic based on the backend-normalised `condition` field.
  // Falls back to old WMO `code` mapping for legacy payloads.
  const cond = w.condition || _legacyCodeToCond(w.code);
  return (
    <div className={`bg-widget bg-widget-weather-live ${className}`} style={style} data-testid="widget-weather">
      <WeatherScene cond={cond} />
      <div className="rc-label text-[9px] opacity-70 mt-1">{(w.location || "").toUpperCase()}</div>
      <div className="font-display rc-glow-text wval">{Math.round(w.temp_c)}°C</div>
      <div className="text-[10px] text-[var(--rc-text-dim)] mt-0.5">{w.description}</div>
    </div>
  );
}

function _legacyCodeToCond(c) {
  if (c == null) return "clear";
  if (c === 0 || c === 1) return "clear";
  if (c === 2) return "partly_cloudy";
  if (c === 3) return "cloudy";
  if ([45, 48].includes(c)) return "fog";
  if ([51, 53, 55, 56, 57].includes(c)) return "drizzle";
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(c)) return "rain";
  if ([71, 73, 75, 77, 85, 86].includes(c)) return "snow";
  if ([95, 96, 99].includes(c)) return "thunder";
  return "clear";
}

function WeatherScene({ cond }) {
  if (cond === "rain" || cond === "drizzle") return <RainScene heavy={cond === "rain"} />;
  if (cond === "snow") return <SnowScene />;
  if (cond === "thunder") return <ThunderScene />;
  if (cond === "fog") return <FogScene />;
  if (cond === "cloudy") return <CloudyScene />;
  if (cond === "partly_cloudy" || cond === "mostly_clear") return <PartlyCloudyScene />;
  return <SunnyScene />;
}

/* ─── Sunny — rotating gold sun with radiating rays ─── */
function SunnyScene() {
  return (
    <svg className="wx-scene" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" data-testid="wx-sunny">
      <defs>
        <radialGradient id="sunGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%"  stopColor="#fff5cc" stopOpacity="1" />
          <stop offset="55%" stopColor="#ffb800" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#ffb800" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Rays — slow rotating group */}
      <g className="wx-spin-slow" style={{ transformOrigin: "60px 60px" }}>
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i / 12) * Math.PI * 2;
          const x1 = 60 + Math.cos(a) * 32; const y1 = 60 + Math.sin(a) * 32;
          const x2 = 60 + Math.cos(a) * 50; const y2 = 60 + Math.sin(a) * 50;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#ffb800" strokeWidth="2.4" strokeLinecap="round" opacity="0.85" />;
        })}
      </g>
      {/* Sun body */}
      <circle cx="60" cy="60" r="40" fill="url(#sunGlow)" />
      <circle cx="60" cy="60" r="22" fill="#ffd34d" />
      <circle cx="60" cy="60" r="22" fill="none" stroke="#fff5cc" strokeWidth="1.2" opacity="0.7" />
    </svg>
  );
}

/* ─── Cloudy & partly cloudy ─── */
function CloudyScene() {
  return (
    <svg className="wx-scene" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" data-testid="wx-cloudy">
      <Cloud x={32} y={62} scale={1.25} drift />
      <Cloud x={68} y={48} scale={0.85} drift slow />
    </svg>
  );
}

function PartlyCloudyScene() {
  return (
    <svg className="wx-scene" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" data-testid="wx-partly-cloudy">
      {/* Small sun in the back */}
      <g style={{ transformOrigin: "82px 36px" }} className="wx-spin-slow">
        {Array.from({ length: 10 }).map((_, i) => {
          const a = (i / 10) * Math.PI * 2;
          const x1 = 82 + Math.cos(a) * 16; const y1 = 36 + Math.sin(a) * 16;
          const x2 = 82 + Math.cos(a) * 24; const y2 = 36 + Math.sin(a) * 24;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#ffb800" strokeWidth="1.6" strokeLinecap="round" opacity="0.8" />;
        })}
      </g>
      <circle cx="82" cy="36" r="14" fill="#ffd34d" />
      <Cloud x={45} y={62} scale={1.1} drift />
    </svg>
  );
}

/* ─── Rainy — cloud + animated rain streaks ─── */
function RainScene({ heavy }) {
  const drops = heavy ? 14 : 8;
  return (
    <svg className="wx-scene" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" data-testid="wx-rain">
      {/* Drift cloud */}
      <g className="wx-drift">
        <Cloud x={60} y={42} scale={1.3} dark />
      </g>
      {/* Rain drops */}
      {Array.from({ length: drops }).map((_, i) => {
        const x = 22 + (i * (76 / drops));
        const delay = (i * 0.18) % 1.4;
        return (
          <line
            key={i}
            x1={x} y1={60} x2={x - 3} y2={72}
            stroke="#7ec8ff" strokeWidth="1.6" strokeLinecap="round"
            className="wx-raindrop"
            style={{ animationDelay: `${delay}s` }}
          />
        );
      })}
      {/* Splash dots at bottom */}
      <g opacity="0.6">
        {Array.from({ length: 4 }).map((_, i) => (
          <circle key={i} cx={28 + i * 22} cy="104" r="1.5" fill="#7ec8ff" className="wx-splash" style={{ animationDelay: `${i * 0.3}s` }} />
        ))}
      </g>
    </svg>
  );
}

/* ─── Snowy — soft puffy cloud + falling flakes ─── */
function SnowScene() {
  const flakes = 14;
  return (
    <svg className="wx-scene" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" data-testid="wx-snow">
      <g className="wx-drift-slow">
        <Cloud x={60} y={42} scale={1.3} puffy />
      </g>
      {Array.from({ length: flakes }).map((_, i) => {
        const x = 18 + (i * (84 / flakes));
        const delay = (i * 0.25) % 3;
        const size = 1.2 + (i % 3) * 0.6;
        return (
          <circle
            key={i}
            cx={x} cy={62} r={size}
            fill="#ffffff" opacity="0.92"
            className="wx-snowflake"
            style={{ animationDelay: `${delay}s` }}
          />
        );
      })}
    </svg>
  );
}

/* ─── Thunder ─── */
function ThunderScene() {
  return (
    <svg className="wx-scene" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" data-testid="wx-thunder">
      <g className="wx-drift">
        <Cloud x={60} y={42} scale={1.3} dark />
      </g>
      <polyline points="58,58 50,76 62,76 54,98 72,72 60,72 68,58"
        fill="none" stroke="#ffb800" strokeWidth="2.2" strokeLinejoin="round"
        className="wx-lightning" />
      {/* a few raindrops too */}
      {Array.from({ length: 6 }).map((_, i) => (
        <line key={i} x1={28 + i * 12} y1={60} x2={26 + i * 12} y2={70}
              stroke="#7ec8ff" strokeWidth="1.4" strokeLinecap="round"
              className="wx-raindrop" style={{ animationDelay: `${i * 0.22}s` }} />
      ))}
    </svg>
  );
}

/* ─── Fog ─── */
function FogScene() {
  return (
    <svg className="wx-scene" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" data-testid="wx-fog">
      <Cloud x={60} y={42} scale={1.2} faint />
      {[58, 72, 86].map((y, i) => (
        <line key={i} x1={20} y1={y} x2={100} y2={y}
              stroke="#cbd3e0" strokeWidth="3" strokeLinecap="round" opacity="0.55"
              className="wx-fog-line" style={{ animationDelay: `${i * 0.4}s` }} />
      ))}
    </svg>
  );
}

/* ─── Cloud SVG primitive ─── */
function Cloud({ x, y, scale = 1, drift = false, slow = false, dark = false, puffy = false, faint = false }) {
  const fill = puffy ? "#f6f8fc" : dark ? "#6b7384" : faint ? "#cbd3e0" : "#dfe5ee";
  const stroke = puffy ? "#cad0db" : dark ? "#3a4252" : "#a7b0c0";
  const cls = drift ? (slow ? "wx-drift-slow" : "wx-drift") : "";
  return (
    <g className={cls} style={{ transformOrigin: `${x}px ${y}px`, transform: `scale(${scale})` }}>
      <ellipse cx={x - 10} cy={y + 4} rx="12" ry="9" fill={fill} stroke={stroke} strokeWidth="1.2" />
      <ellipse cx={x + 10} cy={y + 4} rx="14" ry="11" fill={fill} stroke={stroke} strokeWidth="1.2" />
      <ellipse cx={x} cy={y - 6} rx="14" ry="11" fill={fill} stroke={stroke} strokeWidth="1.2" />
      <rect x={x - 18} y={y + 4} width="38" height="9" rx="4.5" fill={fill} />
    </g>
  );
}

function CryptoTicker({ symbol, label, className, style }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const fetchP = async () => {
      try {
        const r = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${symbol}&vs_currencies=gbp&include_24hr_change=true`);
        if (r.ok) {
          const j = await r.json();
          setData(j[symbol]);
        }
      } catch {}
    };
    fetchP();
    const t = setInterval(fetchP, 60000);
    return () => clearInterval(t);
  }, [symbol]);
  if (!data) return null;
  const up = (data.gbp_24h_change || 0) >= 0;
  return (
    <div className={`bg-widget bg-widget-ticker ${className}`} style={style} data-testid={`widget-${label.toLowerCase()}`}>
      <div className="rc-label text-[9px] opacity-70">{label}/£</div>
      <div className="font-display rc-glow-text wval tabular-nums">£{Math.round(data.gbp).toLocaleString()}</div>
      <div className={`text-[10px] tabular-nums ${up ? "text-emerald-400" : "text-red-400"}`}>{up ? "▲" : "▼"} {(data.gbp_24h_change || 0).toFixed(2)}%</div>
    </div>
  );
}

function GoldTicker({ className, style }) {
  // Free-ish proxy via stooq (XAUUSD). May rate-limit; failure = hidden.
  const [px, setPx] = useState(null);
  useEffect(() => {
    const fetchG = async () => {
      try {
        const r = await fetch("https://stooq.com/q/?s=xauusd&f=sd2t2ohlcv&h&e=csv");
        if (!r.ok) return;
        const txt = await r.text();
        const lines = txt.trim().split("\n");
        if (lines.length >= 2) {
          const row = lines[1].split(",");
          const close = parseFloat(row[6]);
          if (!isNaN(close)) setPx(close);
        }
      } catch {}
    };
    fetchG();
    const t = setInterval(fetchG, 300000);
    return () => clearInterval(t);
  }, []);
  if (!px) return null;
  return (
    <div className={`bg-widget bg-widget-ticker ${className}`} style={style} data-testid="widget-gold">
      <div className="rc-label text-[9px] opacity-70">GOLD/$</div>
      <div className="font-display rc-glow-text wval tabular-nums">${px.toFixed(2)}</div>
    </div>
  );
}

function StockTicker({ symbol, className, style }) {
  const [px, setPx] = useState(null);
  useEffect(() => {
    const fetchS = async () => {
      try {
        const r = await fetch(`https://stooq.com/q/?s=${symbol.toLowerCase()}.us&f=sd2t2ohlcv&h&e=csv`);
        if (!r.ok) return;
        const txt = await r.text();
        const lines = txt.trim().split("\n");
        if (lines.length >= 2) {
          const row = lines[1].split(",");
          const close = parseFloat(row[6]);
          if (!isNaN(close)) setPx(close);
        }
      } catch {}
    };
    fetchS();
    const t = setInterval(fetchS, 120000);
    return () => clearInterval(t);
  }, [symbol]);
  if (!px) return null;
  return (
    <div className={`bg-widget bg-widget-ticker ${className}`} style={style} data-testid={`widget-stock-${symbol}`}>
      <div className="rc-label text-[9px] opacity-70">{symbol.toUpperCase()}/$</div>
      <div className="font-display rc-glow-text wval tabular-nums">${px.toFixed(2)}</div>
    </div>
  );
}

function CustomText({ text, className, style }) {
  return (
    <div className={`bg-widget bg-widget-text ${className}`} style={style} data-testid="widget-custom-text">
      <div className="font-display rc-glow-text wval tracking-widest">{text}</div>
    </div>
  );
}

function LogoImage({ url, className }) {
  if (!url) return null;
  return (
    <div className={`bg-widget bg-widget-logo ${className}`} data-testid="widget-logo">
      <img src={url} alt="logo" />
    </div>
  );
}

function IlluminatiEye({ className, style }) {
  return (
    <div className={`bg-widget bg-widget-eye ${className}`} style={style} data-testid="widget-eye">
      <svg viewBox="0 0 200 180" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="eyeGlow" cx="50%" cy="55%" r="55%">
            <stop offset="0%"  stopColor="#fff5cc" stopOpacity="1" />
            <stop offset="60%" stopColor="#ffb800" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#ffb800" stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* Pyramid */}
        <polygon points="100,16 188,164 12,164" fill="none" stroke="currentColor" strokeWidth="2.2" />
        {/* Inner pyramid line (capstone) */}
        <line x1="38" y1="110" x2="162" y2="110" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1.4" />
        {/* Sun rays around eye */}
        {Array.from({ length: 14 }).map((_, i) => {
          const a = (i / 14) * Math.PI * 2;
          const x1 = 100 + Math.cos(a) * 30;
          const y1 = 75 + Math.sin(a) * 22;
          const x2 = 100 + Math.cos(a) * 44;
          const y2 = 75 + Math.sin(a) * 32;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeOpacity="0.65" strokeWidth="1.1" />;
        })}
        {/* Eye glow */}
        <ellipse cx="100" cy="75" rx="34" ry="18" fill="url(#eyeGlow)" />
        {/* Eye outline */}
        <path d="M62 75 Q100 45 138 75 Q100 105 62 75 Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
        {/* Iris + pupil */}
        <circle cx="100" cy="75" r="9" fill="currentColor" />
        <circle cx="100" cy="75" r="3.5" fill="#000" />
      </svg>
    </div>
  );
}

function MoonPhase({ className, style }) {
  // Approximation: phase 0..1 (0 = new, 0.5 = full)
  const now = new Date();
  const NEW_MOON_REF = new Date(Date.UTC(2000, 0, 6, 18, 14));
  const LP = 29.530588853;
  const days = (now - NEW_MOON_REF) / 86400000;
  const phase = ((days % LP) + LP) % LP / LP;
  // Visual: render circle then offset crescent
  const illum = Math.abs(0.5 - phase) * 2; // 0 (new)..1 (full)
  const isWaxing = phase < 0.5;
  return (
    <div className={`bg-widget bg-widget-moon ${className}`} style={style} data-testid="widget-moon">
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="42" fill="#1a1f2c" stroke="currentColor" strokeOpacity="0.6" strokeWidth="1.2" />
        <circle cx={isWaxing ? 50 + (1 - illum) * 50 : 50 - (1 - illum) * 50} cy="50" r="42" fill="#0a0d14" />
        <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeOpacity="0.8" strokeWidth="1.2" />
      </svg>
      <div className="rc-label text-[8px] mt-1 opacity-70">{Math.round(phase * 100)}%</div>
    </div>
  );
}


function LiveUsersWidget({ appKey = "all", className, style }) {
  const [data, setData] = useState(null);
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    let stopped = false;
    let lastCount = null;
    const fetchN = async () => {
      try {
        const tk = localStorage.getItem("rawco_token");
        if (!tk) return;
        const qs = `?app_key=${encodeURIComponent(appKey)}&days=1`;
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/unique_users${qs}`, {
          headers: { Authorization: `Bearer ${tk}` },
        });
        if (!r.ok || stopped) return;
        const j = await r.json();
        if (stopped) return;
        setData(j);
        if (lastCount !== null && j.unique_users !== lastCount) {
          setPulse(true);
          setTimeout(() => !stopped && setPulse(false), 900);
        }
        lastCount = j.unique_users;
      } catch {}
    };
    fetchN();
    const t = setInterval(fetchN, 30000);
    return () => { stopped = true; clearInterval(t); };
  }, [appKey]);
  if (!data) return null;
  const label = appKey === "all" ? "LIVE USERS" : `${appKey.toUpperCase()} LIVE`;
  return (
    <div className={`bg-widget bg-widget-ticker ${className}`} style={style} data-testid={`widget-live-users-${appKey}`}>
      <div className="rc-label text-[9px] opacity-70">{label}</div>
      <div className={`font-display rc-glow-text wval tabular-nums ${pulse ? "rc-pulse-once" : ""}`}>{data.unique_users}</div>
      <div className="text-[10px] text-[var(--rc-text-dim)] mt-0.5">last 24h · de-duped</div>
    </div>
  );
}

function DashboardIframeWidget({ url, title, className }) {
  if (!url) return null;
  const heights = { wsize_small: 140, wsize_medium: 220, wsize_large: 320 };
  const widths  = { wsize_small: 240, wsize_medium: 360, wsize_large: 520 };
  const sizeKey = (className || "").replace("-", "_").replace("wsize-", "wsize_");
  const h = heights[sizeKey] || 220;
  const w = widths[sizeKey] || 360;

  // Brand metadata derived from URL — keeps the card recognisable even when
  // the underlying site refuses to be embedded (X-Frame-Options) or shows a
  // "rotate to portrait" wall (mobile-first responsive). Owner reported
  // Trader, SnapDog & Clothing showing the rotate wall — so iframes are
  // out. We replace them with branded launch cards.
  const host = (() => { try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return ""; } })();
  const brand = brandFor(host, title);

  const openFullApp = (e) => {
    e?.stopPropagation();
    window.dispatchEvent(new CustomEvent("rawco-open-in-browser", {
      detail: { url, title: title || brand.name }
    }));
  };

  return (
    <div
      className={`bg-widget bg-widget-iframe ${className}`}
      data-testid="widget-dashboard-iframe"
      onClick={openFullApp}
      style={{
        width: w, height: h + 22, padding: 0, overflow: "hidden", position: "relative",
        cursor: "pointer",
      }}
    >
      <div className="rc-label text-[9px] opacity-70 flex items-center justify-between"
           style={{ padding: "4px 8px", borderBottom: "1px solid var(--rc-gold-dim)" }}>
        <span>{title || brand.name}</span>
        <button
          type="button"
          onClick={openFullApp}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          title="Open full app"
          data-testid={`widget-iframe-open-${(title||brand.name).replace(/\s+/g,'-').toLowerCase()}`}
          style={{
            background: "rgba(255,184,0,0.15)", border: "1px solid var(--rc-gold-dim)",
            color: "var(--rc-gold)", padding: "2px 8px", fontSize: 9, letterSpacing: 1.2,
            cursor: "pointer", borderRadius: 3,
          }}
        >OPEN ↗</button>
      </div>
      {/* Branded launch tile — replaces the iframe entirely. Picks a colour
          + icon from the host so SnapDog/Clothing/Trader/RawCompany etc. all
          look distinctive. Click anywhere to launch the full app. */}
      <div style={{
        position: "relative", width: "100%", height: h,
        background: `linear-gradient(135deg, ${brand.bg1} 0%, ${brand.bg2} 100%)`,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: 8, color: brand.fg,
      }}>
        <div style={{
          fontFamily: "'Share Tech Mono', monospace",
          fontSize: Math.min(w / 8, 38), letterSpacing: 4, fontWeight: 700,
          textShadow: `0 0 18px ${brand.glow}`,
        }}>{brand.glyph}</div>
        <div style={{
          fontFamily: "'Share Tech Mono', monospace",
          fontSize: 11, letterSpacing: 3, opacity: 0.85,
        }}>{(title || brand.name).toUpperCase()}</div>
        <div style={{
          marginTop: 4, padding: "4px 10px",
          border: `1px solid ${brand.fg}55`, borderRadius: 999,
          fontSize: 9, letterSpacing: 2, opacity: 0.7,
        }}>TAP TO OPEN</div>
      </div>
    </div>
  );
}

/**
 * Brand palette per app — keeps the launch tile recognisable. Falls back to
 * gold/black for unknown hosts. Owner can recognise each tile at a glance.
 */
function brandFor(host, title) {
  const h = (host || "").toLowerCase();
  const t = (title || "").toLowerCase();
  const has = (s) => h.includes(s) || t.includes(s);
  if (has("snapdog") || has("dogsnap")) return {
    name: "SnapDog",     glyph: "🐕",
    bg1: "#1a0e2e", bg2: "#3d1769", fg: "#d4a8ff", glow: "rgba(180,120,255,0.7)",
  };
  if (has("clothing")) return {
    name: "Clothing",    glyph: "👕",
    bg1: "#0e1a2e", bg2: "#0a3d69", fg: "#a8d4ff", glow: "rgba(120,180,255,0.7)",
  };
  if (has("rawmixes"))  return {
    name: "Raw Mixes",   glyph: "🎧",
    bg1: "#2e1a0e", bg2: "#693d17", fg: "#ffd4a8", glow: "rgba(255,180,120,0.7)",
  };
  if (has("racing") || has("formguide")) return {
    name: "Racing Form Guide", glyph: "🏇",
    bg1: "#0e2e1a", bg2: "#176939", fg: "#a8ffd4", glow: "rgba(120,255,180,0.7)",
  };
  if (has("trader") || has("capital")) return {
    name: "Trader",      glyph: "📈",
    bg1: "#2e0e0e", bg2: "#691717", fg: "#ffa8a8", glow: "rgba(255,120,120,0.7)",
  };
  if (has("rawcompany")) return {
    name: "Raw Company", glyph: "★",
    bg1: "#1a1208", bg2: "#3d2a0e", fg: "#ffd47a", glow: "rgba(255,184,0,0.8)",
  };
  if (has("aicontroles")) return {
    name: "AI Controls", glyph: "◉",
    bg1: "#0a0a14", bg2: "#1a1a2e", fg: "#ffb800", glow: "rgba(255,184,0,0.7)",
  };
  return {
    name: title || host || "App", glyph: "◇",
    bg1: "#0a0a0a", bg2: "#1a1a1a", fg: "#ffb800", glow: "rgba(255,184,0,0.7)",
  };
}
